'use client';

import { c as _c } from "react/compiler-runtime";
import './nav-card.scss';
import { useTranslation } from 'react-i18next';
import { useRandomChild } from '@sola/ark-core/contexts/random-child.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { countryToIso } from '@sola/schema/dotcom/shared/geo.ts';
import type { FlexibleContentProps, SacFilterProps } from '@sola/schema/lib/schema.ts';
import { knownSkippyUrls } from '@sola/skippy-sdk/urls.ts';
import { Image } from '../../atoms/image/image.tsx';
import { ChildBioSummary } from '../child-bio-details/child-bio-summary.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';
import { NavCardSkeleton } from './nav-card-skeleton.tsx';
type NavCardProps = FlexibleContentProps & {
  mobile?: boolean;
  featured?: boolean;
  className?: string;
};
type NavCardSacProps = SacFilterProps & {
  mobile?: boolean;
  featured?: boolean;
  className?: string;
};
const NavCardContent: React.FC<NavCardProps> = t0 => {
  const $ = _c(24);
  let className;
  let featured;
  let media;
  let mobile;
  let props;
  if ($[0] !== t0) {
    const {
      media: t1,
      subtitle: _subtitle,
      icon: _icon,
      quotation: _quotation,
      mobile: t2,
      featured: t3,
      className: t4,
      ...t5
    } = t0;
    media = t1;
    mobile = t2;
    featured = t3;
    className = t4;
    props = t5;
    $[0] = t0;
    $[1] = className;
    $[2] = featured;
    $[3] = media;
    $[4] = mobile;
    $[5] = props;
  } else {
    className = $[1];
    featured = $[2];
    media = $[3];
    mobile = $[4];
    props = $[5];
  }
  let t1;
  if ($[6] !== featured || $[7] !== mobile) {
    t1 = () => {
      if (!featured && mobile) {
        return "square";
      }
      return mobile ? "4-3" : "3-4";
    };
    $[6] = featured;
    $[7] = mobile;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const mediaAspectRatio = t1;
  let t2;
  if ($[9] !== className) {
    t2 = cn("NavCard w-full", className);
    $[9] = className;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== media?.image || $[12] !== mediaAspectRatio) {
    t3 = media?.image && <div className="NavCard__media"><Image image={media?.image} hasPlaceholder={true} aspectRatio={mediaAspectRatio()} objectPosition="top" rounded="md" /></div>;
    $[11] = media?.image;
    $[12] = mediaAspectRatio;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const t4 = !featured ? props.title : undefined;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("NavCard__body");
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== props || $[16] !== t4) {
    t6 = <FeatureBase {...props} title={t4} titleSize="lg" titleVariant="label" bodySize="md" bodyVariant="label" buttonSize="md" primaryCTAVariant="tertiary" primaryCTAIconName="arrow-right" primaryCTAIconPlacement="after" className={t5} variantSpacing="card" alignment="left" inline={true} buttonsAsLinks={true} />;
    $[15] = props;
    $[16] = t4;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t3 || $[19] !== t6) {
    t7 = <div className="NavCard__content flex items-start gap-comfy-lg lg:gap-comfy-sm">{t3}{t6}</div>;
    $[18] = t3;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t2 || $[22] !== t7) {
    t8 = <div className={t2}>{t7}</div>;
    $[21] = t2;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
};
const NavCardSac = props => {
  const $ = _c(9);
  const {
    className
  } = props;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["bio", "geo"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    data: childData,
    isLoading
  } = useRandomChild();
  const countryIso = countryToIso(childData?.country, childData?.localId);
  const countryKey = `geo:countries.${countryIso}.nameS`;
  t(countryKey);
  let t1;
  if ($[1] !== childData || $[2] !== props || $[3] !== t) {
    t1 = childData ? {
      title: childData.name.preferred,
      body: <ChildBioSummary className="NavCard__body" short={true} details={childData} />,
      media: {
        image: {
          url: childData.imageUrl,
          height: 96,
          width: 70,
          alt: t("bio:picAlt", {
            childName: childData.name.preferred
          })
        }
      },
      primaryCTA: {
        title: t("bio:title", {
          childName: childData.name.preferred
        }),
        link: `${knownSkippyUrls.childBio}?gid=${childData.globalId}`
      }
    } : props;
    $[1] = childData;
    $[2] = props;
    $[3] = t;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const cardProps = t1;
  let t2;
  if ($[5] !== cardProps || $[6] !== className || $[7] !== isLoading) {
    t2 = isLoading ? <NavCardSkeleton className={className} /> : <NavCardContent {...cardProps} className={className} />;
    $[5] = cardProps;
    $[6] = className;
    $[7] = isLoading;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
export const NavCard = t0 => {
  const $ = _c(6);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  if ("limit" in props) {
    let t1;
    if ($[2] !== props) {
      t1 = <NavCardSac {...props} />;
      $[2] = props;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== props) {
    t1 = <NavCardContent {...props} />;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};